import React from 'react';
import { checkCondition, ConditionType } from 'component/common/Experiment/Condition';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { useSelector } from 'react-redux';
import { selectConfigFromBootstrap } from 'store/slice/bootstrap';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';

export interface FeatureProps {
    name: FeatureName;
    when?: ConditionType;
    checkFeatureEnabled?: boolean;
}

export type FeatureVariantProps = Omit<FeatureProps, 'checkFeatureEnabled'>;

const isFeatureEnabled = (featureName: FeatureProps['name'], config: BootstrapConfig, checkForBVariant = true): boolean => {
    const featureEnabled = isOn(featureName, config);
    return checkForBVariant ? featureEnabled : !featureEnabled;
};

const FeatureExperiment: React.FC<React.PropsWithChildren<FeatureProps>> = React.memo((props) => {
    const config = useSelector(selectConfigFromBootstrap);
    const conditionSuccess = props.when === null || props.when === undefined ? true : checkCondition(props.when);
    if (!conditionSuccess) {
        return null;
    }

    const featureEnabled = isFeatureEnabled(props.name, config, props.checkFeatureEnabled);
    if (!featureEnabled) {
        return null;
    }
    return <>{props.children}</>;
});
FeatureExperiment.displayName = 'FeatureExperiment';

const FeatureOff: React.FC<React.PropsWithChildren<FeatureVariantProps>> = (props) => {
    return (
        <FeatureExperiment name={props.name} when={props.when} checkFeatureEnabled={false}>
            {props.children}
        </FeatureExperiment>
    );
};
FeatureOff.displayName = 'FeatureOff';

const FeatureOn: React.FC<React.PropsWithChildren<FeatureVariantProps>> = (props) => {
    return (
        <FeatureExperiment name={props.name} when={props.when} checkFeatureEnabled={true}>
            {props.children}
        </FeatureExperiment>
    );
};
FeatureOn.displayName = 'FeatureOn';

const useFeature = (name: FeatureName, config: BootstrapConfig) => {
    return React.useMemo(() => {
        return isFeatureEnabled(name, config);
    }, [config, name]);
};

export const useFeatureEnabled = (name: FeatureName, config: BootstrapConfig) => useFeature(name, config);

export default {
    Off: FeatureOff,
    On: FeatureOn,
};
